import { TrackingEvent } from '@acretrader/acremaps-analytics-events';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/sharp-solid-svg-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import Button, { ButtonSize, ButtonVariant } from '@/components/button';
import Checkbox from '@/components/checkbox';
import OutsideClickWrapper from '@/components/OutsideClickWrapper';
import { RadioVariants } from '@/components/radio';
import RadioGroup from '@/components/radio/radioGroup';
import { AUTH_ACTIONS, GLOBAL_TOOLTIP } from '@/constants/constants';
import {
  parcelFsaOptions,
  ParcelOrFsa,
} from '@/features/navigation/hooks/useParcelFsaBaseLayers';
import { useAnalytics } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/auth-provider';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';

export const ParcelFsaSelector = () => {
  const { track } = useAnalytics();

  const { userCanAccessOwnerNames, isAnonymous } = useAuth();
  const {
    layers: {
      shouldHideParcelOwnerNames,
      setShouldHideParcelOwnerNames,
      parcelOrFsaToRender,
      setParcelOrFsaToRender,
      shouldHideParcelLayer,
    },
  } = useMapboxContext();

  const [showParcelOwnerNameCheckbox, setShowParcelOwnerNameCheckbox] =
    useState(false);

  useEffect(() => {
    if (isAnonymous) {
      setShowParcelOwnerNameCheckbox(true);
    }
  }, [isAnonymous]);

  const disabledOwnerNamesTooltip = useMemo(() => {
    return !userCanAccessOwnerNames ? AUTH_ACTIONS.UPGRADE_TO_ACCESS : '';
  }, [userCanAccessOwnerNames]);

  const handleEnableFsaLayer = useCallback(() => {
    setParcelOrFsaToRender(ParcelOrFsa.FSA);
    track({
      data: { location: 'map bottom nav' },
      event: TrackingEvent.FSA_LAYER_SELECTED,
    });
  }, [setParcelOrFsaToRender, track]);

  const handleEnableParcelLayer = useCallback(() => {
    setParcelOrFsaToRender(ParcelOrFsa.PARCELS);
    track({
      data: { location: 'map bottom nav' },
      event: TrackingEvent.PARCEL_LAYER_SELECTED,
    });
  }, [setParcelOrFsaToRender, track]);

  return (
    <div className="flex h-[40px] w-fit min-w-[160px] items-center rounded-sm bg-white p-2 transition-all ease-in-out">
      <OutsideClickWrapper
        className="flex grow"
        onOutsideClick={() => {
          setShowParcelOwnerNameCheckbox(false);
        }}
      >
        <RadioGroup
          additionalProps={{
            containerClassName: 'flex space-y-0 justify-between space-x-3 mt-0',
          }}
          className="text-gray-70 outline-gray-70"
          options={Object.values(parcelFsaOptions)}
          value={parcelOrFsaToRender}
          variant={RadioVariants.PRIMARY}
          onChange={(val: ParcelOrFsa) => {
            if (val === ParcelOrFsa.FSA) {
              handleEnableFsaLayer();
            } else {
              handleEnableParcelLayer();
            }
          }}
        />
        {parcelOrFsaToRender === ParcelOrFsa.PARCELS && (
          <>
            <Button
              className="flex items-center p-1 hover:cursor-pointer hover:bg-transparent active:bg-transparent"
              size={ButtonSize.XS}
              variant={ButtonVariant.GHOST}
              onClick={() =>
                setShowParcelOwnerNameCheckbox(!showParcelOwnerNameCheckbox)
              }
            >
              <FontAwesomeIcon
                className={twMerge(
                  'ml-2 text-gray-70 transition-transform duration-300',
                  showParcelOwnerNameCheckbox ? 'rotate-180' : 'rotate-0'
                )}
                icon={faChevronRight}
              />
            </Button>
            <div
              className={twMerge(
                'flex w-0 overflow-hidden whitespace-nowrap px-0 transition-all',
                showParcelOwnerNameCheckbox && 'w-[145px] px-2'
              )}
            >
              <div
                data-tooltip-content={disabledOwnerNamesTooltip}
                data-tooltip-id={GLOBAL_TOOLTIP}
              >
                <Checkbox
                  checked={!shouldHideParcelOwnerNames}
                  disabled={!userCanAccessOwnerNames || shouldHideParcelLayer}
                  label="Owner Names"
                  labelClassName="text-gray-70"
                  onChange={(val) => {
                    track({
                      data: {
                        location: 'map bottom nav',
                      },
                      event: val.target.checked
                        ? TrackingEvent.PARCEL_OWNER_NAMES_SHOWN
                        : TrackingEvent.PARCEL_OWNER_NAMES_HIDDEN,
                    });
                    setShouldHideParcelOwnerNames(!val.target.checked);
                  }}
                />
              </div>
            </div>
          </>
        )}
      </OutsideClickWrapper>
    </div>
  );
};
